import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc, setDoc,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';

const TEMPORADAS = [
  { id: 'invierno',   label: 'Invierno',   meses: 'Jun · Jul · Ago',  emoji: '❄️',  color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.25)'  },
  { id: 'primavera',  label: 'Primavera',  meses: 'Sep · Oct · Nov',  emoji: '🌸',  color: '#34d399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.25)'  },
  { id: 'verano',     label: 'Verano',     meses: 'Dic · Ene · Feb',  emoji: '☀️',  color: '#fbbf24', bg: 'rgba(251,191,36,0.1)',  border: 'rgba(251,191,36,0.25)'  },
  { id: 'otono',      label: 'Otoño',      meses: 'Mar · Abr · May',  emoji: '🍂',  color: '#f97316', bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.25)'  },
  { id: 'todo',       label: 'Todo el año', meses: 'Siempre visible', emoji: '📅',  color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.25)' },
];

const MES_A_TEMPORADA = {
  0: 'verano', 1: 'verano', 11: 'verano',
  2: 'otono',  3: 'otono',  4: 'otono',
  5: 'invierno', 6: 'invierno', 7: 'invierno',
  8: 'primavera', 9: 'primavera', 10: 'primavera',
};

const FORM_EMPTY = { icono: '💡', mensaje: '', temporada: 'invierno', activo: true, orden: 0 };

const EMOJIS_SUGERIDOS = ['❄️','☀️','🌸','🍂','🚗','🔧','⚡','💡','🛞','🌧️','💧','🔋','🛣️','⚠️','🔍','🧊','🌡️'];

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate?.() ?? (val instanceof Date ? val : new Date(val));
  if (!d || isNaN(d)) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getTemporadaActual() {
  return MES_A_TEMPORADA[new Date().getMonth()] || 'todo';
}

function TemporadaBadge({ id }) {
  const t = TEMPORADAS.find(t => t.id === id);
  if (!t) return <span className="text-gray-400 text-xs">—</span>;
  return (
    <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold">
      {t.emoji} {t.label}
    </span>
  );
}

/* ── Preview tarjeta app ──────────────────────────────────────────── */
function PreviewCard({ icono, mensaje, temporada }) {
  const t = TEMPORADAS.find(t => t.id === temporada) || TEMPORADAS[0];
  return (
    <div style={{
      borderRadius: 20, border: `1px solid ${t.border}`, backgroundColor: '#0d1629',
      padding: '14px 16px', maxWidth: 232,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 14 }}>🛡</span>
        <p style={{ color: t.color, fontWeight: 900, fontSize: 12, margin: 0 }}>Consejo de temporada</p>
      </div>
      <p style={{ fontSize: 28, margin: '0 0 8px', textAlign: 'center' }}>{icono || '💡'}</p>
      <p style={{ color: '#94a3b8', fontSize: 12, lineHeight: 1.5, margin: 0 }}>
        {mensaje || 'Tu consejo aparecerá aquí...'}
      </p>
    </div>
  );
}

/* ── Modal crear / editar ────────────────────────────────────────── */
function ModalConsejo({ consejo, onClose, onSaved, adminUid }) {
  const isNew = !consejo?.id;
  const [form, setForm] = useState(isNew ? { ...FORM_EMPTY } : {
    icono:     consejo.icono     || '💡',
    mensaje:   consejo.mensaje   || '',
    temporada: consejo.temporada || 'invierno',
    activo:    consejo.activo    ?? true,
    orden:     consejo.orden     ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.mensaje.trim()) { setError('El mensaje es obligatorio.'); return; }
    if (!form.icono.trim())   { setError('El ícono es obligatorio.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        icono:        form.icono.trim(),
        mensaje:      form.mensaje.trim(),
        temporada:    form.temporada,
        activo:       form.activo,
        orden:        Number(form.orden) || 0,
        actualizadoEn: serverTimestamp(),
      };
      if (isNew) {
        payload.creadoEn = serverTimestamp();
        if (adminUid) payload.creadoPor = adminUid;
        await addDoc(collection(db, 'consejos_temporada'), payload);
      } else {
        await updateDoc(doc(db, 'consejos_temporada', consejo.id), payload);
      }
      onSaved();
      onClose();
    } catch (e) {
      setError('Error al guardar: ' + (e.message || e));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/60" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">

          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-base font-black text-gray-900">
              {isNew ? 'Nuevo consejo de temporada' : 'Editar consejo'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>

          <div className="flex flex-col md:flex-row">
            {/* Formulario */}
            <div className="flex-1 p-6 space-y-4">

              {/* Temporada */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Temporada</label>
                <div className="flex flex-wrap gap-2">
                  {TEMPORADAS.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => set('temporada', t.id)}
                      style={form.temporada === t.id ? { background: t.bg, borderColor: t.border, color: t.color } : {}}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                        form.temporada === t.id
                          ? ''
                          : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {t.emoji} {t.label}
                      <span className="font-normal opacity-70">· {t.meses}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Ícono */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Ícono (emoji)</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={form.icono}
                    onChange={e => set('icono', e.target.value)}
                    maxLength={8}
                    className="w-20 text-center text-2xl border border-gray-300 rounded-xl px-2 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {EMOJIS_SUGERIDOS.map(e => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => set('icono', e)}
                        className={`text-lg p-1 rounded-lg transition-all ${form.icono === e ? 'bg-indigo-100 ring-2 ring-indigo-300' : 'hover:bg-gray-100'}`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mensaje */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">
                  Mensaje del consejo
                  <span className="ml-2 font-normal normal-case text-gray-300">{form.mensaje.length}/180 caracteres</span>
                </label>
                <textarea
                  value={form.mensaje}
                  onChange={e => set('mensaje', e.target.value)}
                  rows={3}
                  maxLength={180}
                  placeholder="Ej: Revisa el anticongelante y el sistema de calefacción antes del invierno."
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>

              {/* Orden y activo */}
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Orden (menor = primero)</label>
                  <input
                    type="number"
                    min={0}
                    max={999}
                    value={form.orden}
                    onChange={e => set('orden', e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                </div>
                <div className="pb-0.5">
                  <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Visible en app</label>
                  <button
                    type="button"
                    onClick={() => set('activo', !form.activo)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border font-bold text-xs transition-all ${
                      form.activo
                        ? 'bg-green-50 border-green-200 text-green-700'
                        : 'bg-gray-50 border-gray-200 text-gray-500'
                    }`}
                  >
                    <span className={`w-3 h-3 rounded-full ${form.activo ? 'bg-green-500' : 'bg-gray-300'}`} />
                    {form.activo ? 'Activo' : 'Inactivo'}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{error}</div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-indigo-600 text-white rounded-xl py-2.5 text-sm font-bold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando…' : isNew ? 'Crear consejo' : 'Guardar cambios'}
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="md:w-[260px] bg-slate-900 p-6 flex flex-col gap-3">
              <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Vista previa en app</p>
              <PreviewCard icono={form.icono} mensaje={form.mensaje} temporada={form.temporada} />
              <p className="text-[10px] text-slate-600 leading-relaxed">
                Así se verá en la sección &ldquo;Avisos&rdquo; de la app durante la temporada seleccionada.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Panel de configuración de visibilidad de cards en la app ─────── */
const CONFIG_DOC = 'avisos';
const CONFIG_DEFAULTS = { mostrarCardInfo: true, mostrarCardConsejo: true };

function ConfigVisibilidadCards() {
  const [cfg,     setCfg]     = useState(CONFIG_DEFAULTS);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loadingCfg, setLoadingCfg] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'configuracion_app', CONFIG_DOC)).then(snap => {
      if (snap.exists()) setCfg({ ...CONFIG_DEFAULTS, ...snap.data() });
    }).catch(() => {}).finally(() => setLoadingCfg(false));
  }, []);

  const toggle = (key) => setCfg(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await setDoc(doc(db, 'configuracion_app', CONFIG_DOC), {
        ...cfg,
        actualizadoEn: serverTimestamp(),
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { alert('Error al guardar: ' + e.message); }
    finally { setSaving(false); }
  };

  if (loadingCfg) return null;

  const ITEMS = [
    { key: 'mostrarCardInfo',    label: 'Card de Precios combustible',    desc: 'Precios semanales de bencina y diésel' },
    { key: 'mostrarCardConsejo', label: 'Card de Consejo de temporada',   desc: 'Tip de mantenimiento según la temporada actual' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
        <span className="text-xl">📱</span>
        <div>
          <h3 className="text-sm font-black text-gray-800">Visibilidad de cards en la app</h3>
          <p className="text-xs text-gray-400 mt-0.5">Controla qué tarjetas del carrusel "Info semanal" se muestran en Avisos</p>
        </div>
      </div>
      <div className="px-5 py-4 space-y-3">
        {ITEMS.map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-800">{label}</p>
              <p className="text-xs text-gray-400">{desc}</p>
            </div>
            <button
              type="button"
              onClick={() => toggle(key)}
              className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${cfg[key] ? 'bg-indigo-600' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${cfg[key] ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
        ))}
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3">
        {saved && <span className="text-xs font-semibold text-green-600">✓ Guardado</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 text-white text-xs font-bold px-4 py-2 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────────── */
export default function AdminConsejosTemporada() {
  const { user, rol } = useAuth();
  const isAdmin = rol === 'admin';

  const [consejos, setConsejos]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [filtroTemp, setFiltroTemp]     = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  const [modalEditar, setModalEditar]   = useState(null); // null | {} (new) | { id, ...} (edit)
  const [eliminando, setEliminando]     = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'consejos_temporada'), orderBy('temporada'), orderBy('orden'))
      );
      setConsejos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      // fallback sin orderBy compuesto
      try {
        const snap = await getDocs(collection(db, 'consejos_temporada'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          if (a.temporada !== b.temporada) return (a.temporada || '').localeCompare(b.temporada || '');
          return (a.orden || 0) - (b.orden || 0);
        });
        setConsejos(list);
      } catch (e) { console.error(e); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleEliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el consejo "${c.mensaje.slice(0, 50)}…"?`)) return;
    setEliminando(c.id);
    try {
      await deleteDoc(doc(db, 'consejos_temporada', c.id));
      setConsejos(prev => prev.filter(x => x.id !== c.id));
    } catch (e) { alert('Error al eliminar: ' + e.message); }
    finally { setEliminando(null); }
  };

  const handleToggleActivo = async (c) => {
    try {
      await updateDoc(doc(db, 'consejos_temporada', c.id), {
        activo: !c.activo,
        actualizadoEn: serverTimestamp(),
      });
      setConsejos(prev => prev.map(x => x.id === c.id ? { ...x, activo: !x.activo } : x));
    } catch (e) { alert('Error: ' + e.message); }
  };

  const filtrados = consejos.filter(c => {
    if (filtroTemp   && c.temporada !== filtroTemp) return false;
    if (filtroActivo === 'activo'   && !c.activo)  return false;
    if (filtroActivo === 'inactivo' && c.activo)   return false;
    return true;
  });

  const temporadaActual = getTemporadaActual();
  const statsActivos   = consejos.filter(c => c.activo).length;
  const statsInactivos = consejos.filter(c => !c.activo).length;

  if (!isAdmin) return (
    <div className="p-6 bg-white rounded-lg shadow">
      <p className="text-red-600 font-medium">Acceso denegado.</p>
    </div>
  );

  return (
    <div className="space-y-5">

      {/* Config visibilidad cards app */}
      <ConfigVisibilidadCards />

      {/* Encabezado */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">🌤 Consejos de temporada</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Se muestran en la sección <strong>Avisos</strong> de la app según la temporada actual
            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
              Temporada actual: {TEMPORADAS.find(t => t.id === temporadaActual)?.label}
            </span>
          </p>
        </div>
        <button
          onClick={() => setModalEditar({})}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-indigo-700 transition-colors"
        >
          + Nuevo consejo
        </button>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        {[
          { label: 'Total',    count: consejos.length, color: 'text-gray-700',  bg: 'bg-white border-gray-200' },
          { label: 'Activos',  count: statsActivos,    color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
          { label: 'Inactivos',count: statsInactivos,  color: 'text-gray-400',  bg: 'bg-gray-50 border-gray-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.bg}`}>
            <div className={`text-2xl font-black ${s.color}`}>{s.count}</div>
            <div className="text-xs font-semibold text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1.5">Temporada</label>
          <select value={filtroTemp} onChange={e => setFiltroTemp(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="">Todas</option>
            {TEMPORADAS.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-1.5">Estado</label>
          <select value={filtroActivo} onChange={e => setFiltroActivo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300">
            <option value="">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
        </div>
        {(filtroTemp || filtroActivo) && (
          <button onClick={() => { setFiltroTemp(''); setFiltroActivo(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-lg px-3 py-1.5 mt-5">
            Limpiar
          </button>
        )}
        <div className="ml-auto">
          <button onClick={cargar} className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50">
            Actualizar
          </button>
        </div>
      </div>

      {/* Grid de consejos */}
      {loading ? (
        <div className="py-16 text-center text-gray-400">Cargando consejos…</div>
      ) : filtrados.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <p className="text-5xl">🌤</p>
          <p className="text-gray-500 font-medium">No hay consejos{filtroTemp || filtroActivo ? ' con ese filtro' : ''}.</p>
          <button onClick={() => setModalEditar({})} className="text-indigo-600 font-semibold text-sm hover:underline">
            Crear el primer consejo →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtrados.map(c => {
            const t = TEMPORADAS.find(x => x.id === c.temporada) || TEMPORADAS[0];
            const esActual = c.temporada === temporadaActual || c.temporada === 'todo';
            return (
              <div
                key={c.id}
                className={`bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-md ${
                  !c.activo ? 'opacity-60' : ''
                }`}
                style={{ borderColor: c.activo && esActual ? t.border : undefined }}
              >
                {/* Header card */}
                <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-2xl flex-shrink-0">{c.icono}</span>
                    <div className="min-w-0">
                      <TemporadaBadge id={c.temporada} />
                      {esActual && c.activo && (
                        <span className="ml-1 text-[9px] font-black uppercase text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                          Activo ahora
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Toggle activo */}
                  <button
                    onClick={() => handleToggleActivo(c)}
                    className={`flex-shrink-0 w-9 h-5 rounded-full transition-colors relative ${c.activo ? 'bg-green-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${c.activo ? 'left-4' : 'left-0.5'}`} />
                  </button>
                </div>

                {/* Mensaje */}
                <p className="px-4 pb-3 text-sm text-gray-700 leading-relaxed line-clamp-3">{c.mensaje}</p>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-2 bg-gray-50">
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span>Orden {c.orden ?? 0}</span>
                    {c.creadoEn && <span>· {fmtDate(c.creadoEn)}</span>}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => setModalEditar(c)}
                      className="text-xs font-semibold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-2.5 py-1 transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleEliminar(c)}
                      disabled={eliminando === c.id}
                      className="text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1 transition-colors disabled:opacity-40"
                    >
                      {eliminando === c.id ? '…' : 'Eliminar'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal crear/editar */}
      {modalEditar !== null && (
        <ModalConsejo
          consejo={modalEditar?.id ? modalEditar : null}
          onClose={() => setModalEditar(null)}
          onSaved={cargar}
          adminUid={user?.uid}
        />
      )}
    </div>
  );
}
